import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DatePicker } from 'primeng/datepicker';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Popover, PopoverModule } from 'primeng/popover';
import moment from 'moment';
import { CommonModule } from '@angular/common';
import { DATE_HINTS, RangeValue, ShadowDomStyleInjectorService, TimePeriod } from '@everymatrix-base-components/utils';
import { TooltipModule } from 'primeng/tooltip';


export const endDateRequired: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const dates: Date[] | RangeValue = control.value;
  if (Array.isArray(dates) && dates.length && !dates[1] || dates && typeof dates === 'object' && 'end' in dates && dates.start && !dates.end) {
    return { endDateRequired: true };
  }
  return null;
};

@Component({
  selector: 'lib-dm-range-date-picker-lib',
  templateUrl: './dm-range-date-picker-lib.component.html',
  styleUrls: ['./dm-range-date-picker-lib.component.scss'],
  imports: [
    CalendarModule,
    InputTextModule,
    ReactiveFormsModule,
    PopoverModule,
    FormsModule,
    CommonModule,
    DatePicker,
    TooltipModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DmRangeDatePickerLibComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DmRangeDatePickerLibComponent),
      multi: true,
    },
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class DmRangeDatePickerLibComponent
  implements ControlValueAccessor, OnInit, AfterViewInit, OnChanges
{
  manualDateChange = false;
  @Input() requireEndDate = true;
  /**
   * Emits when datepicker is opened
   */

  @Output() openState: EventEmitter<void> = new EventEmitter();
  /**
   * Label text displayed above the input
   */
  @Input() label = 'Date Picker';

  /**
   * Tooltip message
   */
  @Input() tooltipMessage?: string;
  /**
   * Placeholder text inside the input
   */
  @Input() placeholder = 'DD/MM/YYYY - DD/MM/YYYY';

  /**
   * Disables the input field if set to true
   */
  @Input({ transform: booleanAttribute }) disabled = false;

  /**
   * Marks the input as required if set to true
   */
  @Input({ transform: booleanAttribute }) isRequired = false;

  /**
   * Shows info icon
   */
  @Input({ transform: booleanAttribute }) infoIcon = false;

  /**
   * Determines whether the input should have a default date set.
   * If true, a predefined date will be applied.
   */
  @Input({ transform: booleanAttribute }) hasDefaultDate = false;

  /**
   * Initial value provided by the parent component.
   * Usage: For single date select pass a string representation of a date (e.g. "2025-01-01").
   */
  private _initialValue: RangeValue | null = null;
  @Input()
  set initialValue(value: string | RangeValue | null) {
    if (value) {
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value);
        } catch {
          value = null;
        }
      }

      if (value && typeof value === 'object' && 'alias' in value) {
        this._initialValue = value as RangeValue;

        if (this._initialValue.alias !== 'custom') {
          this.onListSelect(this._initialValue.alias as TimePeriod);
        } else {
          this.dateControl.setValue([
            new Date(this._initialValue.start),
            new Date(this._initialValue.end),
          ]);
        }
      }
    }
  }
  get initialValue(): RangeValue | null {
    return this._initialValue;
  }

  /**
   * The minimum selectable date.
   * Usage: Pass a date string (e.g. "2025-01-01") or a Date object.
   */
  private _minDate!: Date;
  @Input()
  set minDate(value: string | Date) {
    if (value) {
      this._minDate = typeof value === 'string' ? new Date(value) : value;
    }
  }
  get minDate(): Date {
    return this._minDate;
  }

  /**
   * The maximum selectable date.
   * Usage: Pass a date string (e.g. "2025-12-31") or a Date object.
   */
  private _maxDate = new Date();
  @Input()
  set maxDate(value: string | Date) {
    if (value) {
      this._maxDate = typeof value === 'string' ? new Date(value) : value;
    }
  }
  get maxDate(): Date {
    return this._maxDate;
  }

  /**
   * Array with dates that should be disabled (not selectable).
   * Usage: Pass a date string (e.g. "2025-05-01") or a Date object.
   * If no date should be disabled, pass null.
   */
  private _disabledDates: Date | null = null;
  @Input()
  set disabledDates(value: string | Date | null) {
    if (value === null || value === undefined || value === '') {
      this._disabledDates = null;
    } else {
      this._disabledDates = typeof value === 'string' ? new Date(value) : value;
    }
  }
  get disabledDates(): Date | null {
    return this._disabledDates;
  }

  /**
   * Array with weekday numbers that should be disabled (not selectable).
   * Usage: Pass a JSON stringified array of numbers (e.g. "[0,6]") or an array of numbers.
   */
  private _disabledDays: number[] | null = null;
  @Input()
  set disabledDays(value: string | number[] | null) {
    if (typeof value === 'string') {
      try {
        this._disabledDays = JSON.parse(value);
      } catch {
        this._disabledDays = null;
      }
    } else {
      this._disabledDays = value;
    }
  }
  get disabledDays(): number[] | null {
    return this._disabledDays;
  }

  /**
   * Format of the date.
   */
  @Input() dateFormat = 'DD/MM/YYYY';

  /**
   * Shows time picker.
   */
  @Input({ transform: booleanAttribute }) showTimePicker = false;

  /**
   * Shows clear button.
   */
  @Input({ transform: booleanAttribute }) showClear = true;

  @ViewChild('op', { static: true }) popover!: Popover;

  currentAlias!: TimePeriod | null;
  inputValue = '';
  listOptions: { key: TimePeriod; value: string }[] = [
    { key: TimePeriod.Today, value: 'Today' },
    { key: TimePeriod.Yesterday, value: 'Yesterday' },
    { key: TimePeriod.CurrentWeek, value: 'Current Week' },
    { key: TimePeriod.CurrentMonth, value: 'Current Month' },
    { key: TimePeriod.CurrentQuarter, value: 'Current Quarter' },
    { key: TimePeriod.LastMonth, value: 'Last Month' },
    { key: TimePeriod.LastQuarter, value: 'Last Quarter' },
    { key: TimePeriod.Last30Days, value: 'Last 30 Days' },
    { key: TimePeriod.CurrentYear, value: 'Current Year' },
  ];

  dateControl: FormControl<Date[] | null> = new FormControl<Date[] | null>(
    null
  );
  @Input() control: any;
  attachedToShadowDom = signal(false);
  popoverOpened = false;

  // If the click did not occur inside this component's Shadow DOM,
  // hide the popover to ensure it closes when clicking outside.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const path = event.composedPath ? event.composedPath() : [];
    if (this.popoverOpened && !path.includes(this.elementRef.nativeElement)) {
      this.onPopoverClose();
    }
  }

  constructor(
    private elementRef: ElementRef,
    private shadowDomStyleInjectorService: ShadowDomStyleInjectorService
  ) {
    this.dateControl.valueChanges.subscribe((dates: Date[] | null) => {
      const start = Array.isArray(dates) && this.isDateValid(dates[0]);
      const end = Array.isArray(dates) && this.isDateValid(dates[1]);
      const parsed: Date[] = [];
      if(start) parsed.push(dates[0]);
      if(start && end) parsed.push(dates[1]);

      const rangeValue: RangeValue = {
        alias: this.currentAlias ?? 'custom',
        start: dates?.[0] ?? null,
        end: dates?.[1] ?? null,
      } as RangeValue;

      this.inputValue = this.formatDateRange(parsed);
      this.onChangeFn(parsed.length ? rangeValue : null);
      this.elementRef.nativeElement.dispatchEvent(
        new CustomEvent('dm-range-date-picker-changed', {
          detail: parsed.length ? rangeValue : null,
          bubbles: true,
          composed: true,
        })
      );
    });
  }

  private onChangeFn: (value: RangeValue | null) => void = () => {};
  private onTouchedFn: () => void = () => {};
  private _onValidatorChange!: () => void;

  ngOnInit(): void {
    if (this.hasDefaultDate && this._initialValue) {
      if (this._initialValue.alias !== 'custom') {
        this.onListSelect(this._initialValue.alias as TimePeriod);
      } else {
        this.dateControl.setValue([
          new Date(this._initialValue.start),
          new Date(this._initialValue.end),
        ]);
      }
    }
    if (this.requireEndDate) {
      this.dateControl.addValidators(endDateRequired);
      this.dateControl.updateValueAndValidity();
    }
  }

  ngAfterViewInit() {
    this.shadowDomStyleInjectorService
      .injectNgPrimeStyles(this.elementRef)
      .subscribe((data) => this.attachedToShadowDom.set(data));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['requireEndDate']) {
      this._onValidatorChange?.();
    }
  }

  openDatePicker(event: Event, element: Popover): void {
    setTimeout(() => {
      this.openState.emit();
      element.toggle(event);
    });
  }

  registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.requireEndDate ? endDateRequired(control) : null;
  }

  writeValue(value: RangeValue | null): void {
    if (!value || typeof value !== 'object' || !('alias' in value) || !value.alias) {
      this.inputValue = '';
      this.dateControl.setValue(null, { emitEvent: false });
      return;
    }

    if (value.alias !== 'custom') {
      this.onListSelect(value.alias as TimePeriod);
    } else {
      const start = this.isDateValid(value.start);
      const end = this.isDateValid(value.end);
      const parsed: Date[] = [];
      if(start) parsed.push(value.start);
      if(start && end) parsed.push(value.end);
      this.currentAlias = parsed.length ? TimePeriod.Custom : null;
      this.dateControl.setValue(parsed.length ? parsed : null, { emitEvent: false });
      this.inputValue = this.formatDateRange(parsed);
    }
  }

  registerOnChange(fn: (value: RangeValue | null) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  dateSelection(): void {
    const range = this.dateControl.value;
    if (!this.showTimePicker && range && Array.isArray(range) && range.length === 2 && range[1] && moment(range[1]).isValid()) {
      const endDate = new Date(range[1]);
      endDate.setHours(23, 59, 59, 999);
      this.dateControl.setValue([range[0], endDate]);
    }
    this.inputValue = this.formatDateRange(this.dateControl.value as Date[]);
    this.currentAlias = TimePeriod.Custom;
  }

  onListSelect(key: TimePeriod): void {
    this.currentAlias = null;
    const alias = DATE_HINTS.find((item) => item.dateAlias === key);
    if (alias) {
      this.currentAlias = key;
      const dates: Date[] = [alias.interval.start, alias.interval.end];
      this.dateControl.setValue(dates);
    }
  }

  private isDateValid(date: Date | null | undefined): boolean {
    return !!date && moment(date).isValid();
  }

  private formatDate(date: Date | null | undefined): string {
    if (!this.isDateValid(date)) {
      return '';
    }
    return moment(date).format('DD/MM/YYYY');
  }

  private formatDateRange(dates: Date[]): string {
    const [date1, date2] = dates || [];
    const firstDateStr = this.formatDate(date1);
    const secondDateStr = this.formatDate(date2);
    if (firstDateStr && secondDateStr) {
      return `${firstDateStr} - ${secondDateStr}`;
    } else if (firstDateStr) {
      return firstDateStr + ' -';
    } else {
      return '';
    }
  }

  protected onPopoverShow(): void {
    const popEl = this.popover.container as HTMLElement;
    const parentEl = popEl.parentElement as HTMLElement;
    this.popoverOpened = true;

    if (popEl && parentEl) {
      popEl.style.left = '0px';
      popEl.style.top = `${parentEl.offsetHeight}px`;
      popEl.style.bottom = '';

      requestAnimationFrame(() => {
        const rect = popEl.getBoundingClientRect();
        const parentRect = parentEl.getBoundingClientRect();
        const popoverHeight = popEl.offsetHeight;

        const viewportHeight = window.innerHeight;
        const viewportWidth =  window.innerWidth;
        const isClippedRight = rect.right > viewportWidth;
        const isClippedBottom = rect.bottom > viewportHeight;
        const spaceAbove = parentRect.top;

        if (isClippedRight) {
          popEl.style.left = `-${rect.width - parentRect.width}px`;
        }

        if (isClippedBottom) {
          if (spaceAbove >= popoverHeight) {
            popEl.style.top = '';
            popEl.style.bottom = `${parentEl.offsetHeight}px`;
          } else {
            popEl.style.top = `-${(viewportHeight - (viewportHeight - popoverHeight)) / 2}px`;
          }
        }
      });
    }
    if(!this.isControlTouched){
      this.dateControl.markAsTouched();
      this.control?.markAsTouched();
    }
  }

  protected onPopoverClose(): void {
    if (this.popover) {
      this.popover.hide();
    }
    this.popoverOpened = false;
  }

  protected onClear(): void {
    this.dateControl.reset();
    this.dateControl.markAsTouched();
    this.dateControl.markAsDirty();
    this.inputValue = '';
    this.currentAlias = null;
    if (this.control) {
      this.control.reset();
      this.control.markAsTouched();
      this.control.markAsDirty();
    }
  }

  protected hasError(errorName: string){
    return this.dateControl.hasError(errorName) || this.control?.hasError(errorName)
  }

  get isControlInvalid() {
    return this.dateControl.invalid || this.control?.invalid;
  }

  get isControlDirty() {
    return this.dateControl.dirty || this.control?.dirty;
  }

  get isControlTouched() {
    return this.dateControl.touched || this.control?.touched;
  }
}
